import { QueryClient } from '@tanstack/react-query'

/**
 * Cache key constants to prevent typos and ensure consistency
 */
export const CACHE_KEYS = {
  // Campaign keys
  CAMPAIGNS: 'campaigns',
  CAMPAIGN: 'campaign',
  CAMPAIGN_SUMMARY: 'campaignSummary',
  CAMPAIGN_GROUPS: 'campaignGroups',
  CAMPAIGN_PERFORMANCE: 'campaignPerformance',
  CAMPAIGN_PERFORMANCE_OVER_TIME: 'campaignPerformanceOverTime',
  LATEST_PURCHASE_DATE_RANGE: 'latestPurchaseDateRange',

  // End Users keys
  END_USERS: 'endUsers',
  TEST_END_USERS: 'testEndUsers',
  END_USERS_PER_CAMPAIGN: 'endUsersPerCampaign',

  // Incentive keys
  INCENTIVES: 'incentives',
  INCENTIVE_BY_ID: 'incentiveById',
  INCENTIVES_BY_CATEGORY: 'incentivesByCategory',
  INCENTIVES_STORE: 'incentivesStore',
  INCENTIVES_PER_CAMPAIGN: 'incentivesPerCampaign',
  INCENTIVE_CATEGORIES: 'incentiveCategories',

  // SKU keys
  SKU_CODE: 'skuCode',
  SKU_CODES: 'skuCodes',

  // Dashboard keys
  DASHBOARD_CAMPAIGNS: 'dashboard-campaigns',
  DASHBOARD_CUSTOMERS: 'dashboard-customers', // ts-rest hook, stores full response object
  DASHBOARD_CAMPAIGN_GROUPS: 'dashboard-campaign-groups', // ts-rest hook, stores full response object
  HAPPINESS_CHECK: 'happinessCheck',
  VOUCHERS_INFO: 'vouchersInfo',

  // Other keys
  CUSTOMERS: 'customers', // Stores extracted array data
  CURRENT_USER: 'currentUser',
  EMAIL_TEMPLATE: 'emailTemplate',
  ONE_COMMUNITY_FORMS: 'oneCommunityForms',
  ONE_COMMUNITY_CAMPAIGNS: 'oneCommunityCampaigns',
  SMALL_CALENDAR_CAMPAIGNS: 'small-calendar-campaigns',
  CALENDAR_CAMPAIGNS: 'calendar-campaigns',
} as const

/**
 * Helper function to invalidate all campaign-related queries
 */
export function invalidateCampaignQueries(
  queryClient: QueryClient,
  campaignId?: string,
) {
  if (campaignId) {
    // Invalidate specific campaign
    queryClient.invalidateQueries({
      queryKey: [CACHE_KEYS.CAMPAIGN, campaignId],
    })
    // Also invalidate dashboard data for this campaign
    queryClient.invalidateQueries({
      queryKey: [CACHE_KEYS.END_USERS_PER_CAMPAIGN, campaignId],
    })
    queryClient.invalidateQueries({
      queryKey: [CACHE_KEYS.INCENTIVES_PER_CAMPAIGN, campaignId],
    })
    queryClient.invalidateQueries({
      queryKey: [CACHE_KEYS.CAMPAIGN_PERFORMANCE, campaignId],
    })
    queryClient.invalidateQueries({
      queryKey: [CACHE_KEYS.CAMPAIGN_PERFORMANCE_OVER_TIME, campaignId],
    })
    queryClient.invalidateQueries({
      queryKey: [CACHE_KEYS.HAPPINESS_CHECK, campaignId],
    })
    queryClient.invalidateQueries({
      queryKey: [CACHE_KEYS.VOUCHERS_INFO, campaignId],
    })
    queryClient.invalidateQueries({
      queryKey: [CACHE_KEYS.TEST_END_USERS, campaignId],
    })

    queryClient.invalidateQueries({
      queryKey: [CACHE_KEYS.INCENTIVES_BY_CATEGORY],
    })
  }

  // Always invalidate the campaigns list
  queryClient.invalidateQueries({
    queryKey: [CACHE_KEYS.CAMPAIGNS],
  })
  queryClient.invalidateQueries({
    queryKey: [CACHE_KEYS.DASHBOARD_CAMPAIGNS],
  })
  queryClient.invalidateQueries({
    queryKey: [CACHE_KEYS.CALENDAR_CAMPAIGNS],
  })
  queryClient.invalidateQueries({
    queryKey: [CACHE_KEYS.SMALL_CALENDAR_CAMPAIGNS],
  })
}

/**
 * Helper function to invalidate incentive-related queries with specific parameters
 */
export function invalidateIncentiveQueries(
  queryClient: QueryClient,
  options?: {
    customerId?: string
    categoryId?: string
    campaignId?: string
    incentiveId?: string
  },
) {
  const { campaignId, incentiveId } = options || {}

  // refetchType: 'all' so list queries refetch even while unmounted — the
  // global client sets refetchOnMount: false, so otherwise the incentive store
  // the user navigates back to after a save renders stale (old isActive etc.).
  // Returns the combined promise so callers can await fresh data before routing.
  const invalidations = [
    queryClient.invalidateQueries({
      queryKey: [CACHE_KEYS.INCENTIVES_BY_CATEGORY],
      refetchType: 'all',
    }),
    queryClient.invalidateQueries({
      queryKey: [CACHE_KEYS.INCENTIVES_STORE],
      refetchType: 'all',
    }),
    queryClient.invalidateQueries({
      queryKey: [CACHE_KEYS.INCENTIVES],
      refetchType: 'all',
    }),
  ]

  if (incentiveId) {
    invalidations.push(
      queryClient.invalidateQueries({
        queryKey: [CACHE_KEYS.INCENTIVE_BY_ID, incentiveId],
        refetchType: 'all',
      }),
    )
  }

  if (campaignId) {
    invalidations.push(
      queryClient.invalidateQueries({
        queryKey: [CACHE_KEYS.INCENTIVES_PER_CAMPAIGN, campaignId],
        refetchType: 'all',
      }),
    )
  }

  return Promise.all(invalidations)
}
